<template>
  <AwModal
    :open="open"
    title="Informations du document"
    size="md"
    position="right"
    @update:open="if ($event === false) handleClose();"
    @close-prevent="handleClose"
  >
    <template #leftPanel>
      <FilePreview v-if="selectedFile" :file="selectedFile" class="min-w-125" />
    </template>

    <template #body>
      <!-- Files Section -->
      <section class="section">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-secondary-900 font-semibold text-lg truncate">{{ title }}</h2>
          <div class="flex items-center gap-2">
            <!-- Replace button (only when no status banner) -->
            <AwButton
              v-if="!statusBannerConfig && documentType && !replaced"
              label="Remplacer"
              size="sm"
              color="secondary"
              variant="outline"
              @click="emit('replace', documentType)"
            />
            <AwDropdownMenu
              v-else-if="!statusBannerConfig && allowedDocumentTypes && !replaced"
              :items="
                allowedDocumentTypes.map((type) => ({
                  label: getHandler(type).documentName,
                  onClick: () => emit('replace', type),
                }))
              "
            >
              <AwButton
                label="Remplacer par"
                size="sm"
                color="secondary"
                variant="outline"
                trailing-icon="i-ph-caret-down"
              />
            </AwDropdownMenu>

            <AwDropdownMenu :items="actionItems">
              <AwButton leading-icon="i-ph-dots-three-vertical" size="sm" color="secondary" variant="outline" />
            </AwDropdownMenu>
          </div>
        </div>

        <AwBadge
          v-if="replaced"
          label="Archivé"
          leading-icon="i-ph-archive"
          color="secondary"
          is-rounded
          class="mb-3 self-start"
        />
        <DocumentStatusBadge
          v-else
          :lifecycle-status="lifecycleStatus"
          :days-before-expiration="daysBeforeExpiration"
          :expiration-date="expirationDate"
          :internal-reviews="internalReviews"
          :external-reviews="externalReviews"
          class="mb-3"
        />

        <!-- No files error (blocking) -->
        <AwBanner
          v-if="statusBannerConfig"
          :color="statusBannerConfig.color"
          :icon="statusBannerConfig.icon"
          :text="statusBannerConfig.text"
          class="mb-4"
        >
          <template #action>
            <!-- Replace button (single type) -->
            <AwButton
              v-if="documentType"
              label="Remplacer"
              size="sm"
              :color="statusBannerConfig.buttonColor"
              variant="solid"
              @click="emit('replace', documentType)"
            />

            <!-- Replace dropdown (multiple types) -->
            <AwDropdownMenu
              v-else-if="allowedDocumentTypes"
              :items="
                allowedDocumentTypes.map((type) => ({
                  label: getHandler(type).documentName,
                  onClick: () => emit('replace', type),
                }))
              "
            >
              <AwButton
                label="Remplacer par"
                size="sm"
                :color="statusBannerConfig.buttonColor"
                variant="solid"
                trailing-icon="i-ph-caret-down"
              />
            </AwDropdownMenu>
          </template>
        </AwBanner>

        <div class="file-preview-list">
          <FileItem
            v-for="(file, index) in files"
            :key="index"
            :file="file"
            :is-selected="index === selectedFileIndex"
            @click="selectedFileIndex = index"
          />
        </div>
      </section>

      <!-- Validation Form Section -->
      <section v-if="files.length > 0" class="section">
        <h3 class="text-secondary-900 font-semibold mb-3">Données vérifiées</h3>

        <!-- Loading State -->
        <AwBanner
          v-if="isLoading"
          color="primary"
          icon="i-ph-spinner-gap-bold"
          class="mb-2"
          text="Données en cours d'extraction..."
        />

        <!-- Readonly Fields -->
        <div v-else class="view-form">
          <DetailsCard
            v-for="field in visibleFields"
            :key="String(field.key)"
            :label="field.label"
            :copy-text="getCopyText(field)"
            :style="{ gridColumn: `span ${field.colSpan ?? 2}` }"
          >
            <IbanGroupedCopy v-if="field.type === 'iban'" :iban="getRawValue(field)" />
            <template v-else>
              <TextEllipsis>{{ getDisplayValue(field) }}</TextEllipsis>
            </template>
          </DetailsCard>
        </div>
      </section>
    </template>
  </AwModal>
</template>

<script setup lang="ts">
import type { AwDropdownMenuItem } from "@asap/design";
import { normalizeIban } from "@asap/shared/utils/iban2";
import { format } from "date-fns";
import { getHandler } from "../handlers/registry";
import type { BaseValidationField, SelectFieldConfig, RadioFieldConfig, DocumentType } from "../handlers/types";
import DocumentStatusBadge from "./DocumentStatusBadge.vue";
import type { Props as DocumentStatusBadgeProps } from "../record/DocumentStatusBadge.vue";
import IbanGroupedCopy from "~/components/shared/iban-input/IbanGroupedCopy.vue";

type BaseProps = {
  open: boolean;
  title: string;
  files: File[];
  formFields: readonly BaseValidationField[];
  data?: Record<string, unknown> | null;
  isLoading?: boolean;
  documentType?: DocumentType;
  allowedDocumentTypes?: DocumentType[];
  allowDelete?: boolean;
  replaced?: boolean;
};

type Props = BaseProps & DocumentStatusBadgeProps;

type Emits = {
  "update:open": [value: boolean];
  "request-close": [];
  download: [];
  replace: [documentType: DocumentType];
  delete: [];
};

const props = withDefaults(defineProps<Props>(), {
  data: () => ({}),
  documentType: undefined,
  allowedDocumentTypes: undefined,
  allowDelete: true,
  replaced: false,
  lifecycleStatus: undefined,
  daysBeforeExpiration: undefined,
  expirationDate: undefined,
  internalReviews: () => [],
  externalReviews: () => [],
});
const emit = defineEmits<Emits>();

// File preview state
const selectedFileIndex = ref(0);
const selectedFile = computed(() => props.files[selectedFileIndex.value]);

// Status banner configuration
type StatusBannerConfig = {
  color: "warning" | "critical";
  icon: string;
  text: string;
  buttonColor: "secondary" | "critical";
};

const statusBannerConfig = computed<StatusBannerConfig | null>(() => {
  if (props.replaced) return null;
  if (props.lifecycleStatus === "expiring_soon") {
    return {
      color: "warning",
      icon: "i-ph-clock-duotone",
      text: "Le document arrive à expiration, remplace-le.",
      buttonColor: "secondary",
    };
  }
  if (props.lifecycleStatus === "expired") {
    return {
      color: "critical",
      icon: "i-ph-seal-warning-fill",
      text: "Le document a expiré, remplace-le.",
      buttonColor: "critical",
    };
  }
  return null;
});

const actionItems = computed(() => {
  const items: AwDropdownMenuItem[] = [
    {
      label: "Télécharger tous les fichiers",
      icon: "i-ph-download-simple",
      onClick: () => emit("download"),
    },
  ];

  if (props.allowDelete && !props.replaced) {
    items.push({
      label: "Supprimer le document",
      icon: "i-ph-trash",
      color: "critical",
      onClick: () => emit("delete"),
    });
  }

  return items;
});

const visibleFields = computed(() => {
  const formValues = Object.fromEntries(props.formFields.map((field) => [String(field.key), getRawValue(field)]));
  return props.formFields.filter((field) => !field.visible || field.visible(formValues));
});

function getRawValue(field: BaseValidationField): string {
  const rawValue = props.data?.[String(field.key)];
  if (rawValue === null || rawValue === undefined) return "";
  return String(rawValue);
}

function getCopyText(field: BaseValidationField): string {
  const rawValue = getRawValue(field);
  if (!rawValue) return "";
  if (field.type === "iban") {
    return normalizeIban(rawValue);
  }
  return getDisplayValue(field);
}

function getDisplayValue(field: BaseValidationField): string {
  const stringValue = getRawValue(field);
  if (!stringValue) return "";

  if (field.type === "select") {
    const selectField = field as SelectFieldConfig;
    return selectField.options.find((o) => o.value === stringValue)?.label ?? stringValue;
  }

  if (field.type === "radio") {
    const radioField = field as RadioFieldConfig;
    return radioField.options.find((o) => o.value === stringValue)?.label ?? stringValue;
  }

  if (field.type === "date" && stringValue) {
    return format(new Date(stringValue), "dd/MM/yyyy");
  }

  if (field.type === "text" && field.format) {
    return field.format(stringValue);
  }

  return stringValue;
}

function handleClose() {
  emit("request-close");
}

function forceClose() {
  emit("update:open", false);
}

defineExpose({ forceClose });
</script>

<style scoped lang="scss">
// Main content sections
.section {
  padding: 1rem;
}

.file-preview-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.view-form {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 1rem;
}
</style>
